import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { PageSection, CheckGrid, InfoCards, CTABand, DisclaimerBox } from "../components/site/ContentBlocks";
import { serviceLd } from "../components/site/SEO";
import { FadeUp } from "../components/site/Reveal";
import { TRADING_SUPPORT, PRODUCT_PAGES, TRADING_DISCLAIMER } from "../data/content";

export default function Trading() {
    return (
        <div className="grain bg-navy-deep" data-testid="trading-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "International Trading Company | Buyers, Suppliers & Coordination — Viet Medica Ltd",
                        description:
                            "Viet Medica Ltd connects qualified buyers, suppliers, distributors and commercial partners across selected energy, agricultural and food-product markets.",
                        path: "/international-trading",
                        jsonLd: serviceLd(
                            "International Trading Coordination",
                            "Buyer and supplier introductions and commercial coordination across energy, agricultural and food-product markets.",
                            "/international-trading"
                        ),
                    }}
                    crumbs={[{ label: "International Trading" }]}
                    eyebrow="International Trading"
                    title="Connecting Products,"
                    titleEm="Partners and Markets."
                    copy="Viet Medica Ltd works through an international network of buyers, suppliers, distributors, mandates and commercial partners across selected energy, agricultural and food-product categories."
                />

                <PageSection eyebrow="How We Support Trade" title="What Our Coordination Covers" testid="trading-support">
                    <CheckGrid items={TRADING_SUPPORT} cols={3} />
                </PageSection>

                <PageSection light eyebrow="Product Categories" title="Selected Global Product Categories" testid="trading-categories">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        {PRODUCT_PAGES.map((p, i) => (
                            <FadeUp key={p.id} delay={(i % 2) * 0.08} className="h-full">
                                <Link
                                    to={p.path}
                                    data-testid={`trading-category-${p.id}`}
                                    className="group relative flex h-[300px] flex-col justify-end overflow-hidden border border-navy-deep/10"
                                >
                                    <img
                                        src={p.image}
                                        alt={p.imageAlt}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/40 to-transparent" aria-hidden="true" />
                                    <div className="relative flex items-end justify-between p-7">
                                        <h3 className="font-serif text-3xl font-light text-ivory">{p.title}</h3>
                                        <ArrowUpRight size={18} className="text-gold transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                </PageSection>

                <PageSection eyebrow="For Each Side of the Trade" title="Buyers and Suppliers" testid="trading-sides">
                    <InfoCards
                        cards={[
                            {
                                title: "For Buyers",
                                copy: "Submit a clear requirement. We verify, match suitable supply connections and coordinate quotations and approved documents through due diligence and contracting.",
                            },
                            {
                                title: "For Suppliers",
                                copy: "Share verified company and product information. We match genuine buyer requirements and coordinate introductions and commercial communication.",
                            },
                        ]}
                        cols={2}
                    />
                    <div className="mt-8 flex flex-wrap gap-4">
                        <Link to="/buyers" data-testid="trading-buyers-link" className="inline-flex items-center gap-2 border border-gold/50 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-navy-deep">
                            How We Work with Buyers <ArrowUpRight size={14} />
                        </Link>
                        <Link to="/suppliers" data-testid="trading-suppliers-link" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors hover:border-gold hover:text-gold">
                            How We Work with Suppliers <ArrowUpRight size={14} />
                        </Link>
                        <Link to="/trading-process" data-testid="trading-process-link" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors hover:border-gold hover:text-gold">
                            Trading Process <ArrowUpRight size={14} />
                        </Link>
                        <Link to="/compliance-due-diligence" data-testid="trading-compliance-link" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors hover:border-gold hover:text-gold">
                            Compliance &amp; Due Diligence <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </PageSection>

                <section className="bg-navy-surface py-20 lg:py-28">
                    <div className="mx-auto max-w-[1440px] space-y-10 px-5 lg:px-10">
                        <DisclaimerBox text={TRADING_DISCLAIMER} testid="trading-disclaimer" />
                        <CTABand
                            title="Submit a clear requirement."
                            primary={{ label: "Trading Enquiry", to: "/trading-enquiry", testid: "trading-enquiry-cta" }}
                            secondary={{ label: "Sourcing from Vietnam", to: "/vietnam-sourcing-agent", testid: "trading-vietnam-cta" }}
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
